import { useEffect, useState } from 'react';
import { API_BASE } from './config';

// =============================================================================
// 대한민국 공휴일 — 서버가 공공데이터포털에서 받아 둔 목록(날짜 → 이름).
// 전원 설정 카드와 스케줄 달력이 같은 목록을 쓰므로 한 번 받아 두고 나눠 쓴다.
// 받기에 실패하면 캐시하지 않아 다음 화면에서 다시 시도한다.
// =============================================================================

export type HolidayNames = ReadonlyMap<string, string>;
const EMPTY: HolidayNames = new Map();
let cache: HolidayNames | null = null;
let inflight: Promise<HolidayNames | null> | null = null;

function load(): Promise<HolidayNames | null> {
  if (cache) return Promise.resolve(cache);
  inflight ??= (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/devices/holidays`);
      if (!response.ok) return null;
      const body = await response.json() as { holidays?: unknown };
      if (!Array.isArray(body.holidays)) return null;
      const names = new Map<string, string>();
      for (const item of body.holidays as Array<{ date?: unknown; name?: unknown }>) {
        if (item && typeof item.date === 'string' && typeof item.name === 'string') names.set(item.date, item.name);
      }
      cache = names;
      return names;
    } catch { return null; }
    finally { inflight = null; }
  })();
  return inflight;
}

/** `enabled` 가 false 면 받지 않는다 — 공휴일 규칙이 없는 화면은 요청할 이유가 없다. */
export function usePublicHolidays(enabled = true): { holidays: HolidayNames; loaded: boolean } {
  const [state, setState] = useState<{ holidays: HolidayNames; loaded: boolean }>(
    () => cache ? { holidays: cache, loaded: true } : { holidays: EMPTY, loaded: false });
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    void load().then(names => { if (!stopped) setState({ holidays: names ?? EMPTY, loaded: true }); });
    return () => { stopped = true; };
  }, [enabled]);
  return state;
}
